import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { UserPlus, Check, Building2, Loader2 } from 'lucide-react';
import { usersService } from '../../services/users';
import { gymMembersService } from '../../services/gymMembers';
import { useToast } from '../ui/use-toast';
import type { UserQrCodeVerification } from '../../services/users';
import type { UserStaff } from '../../types/user';

interface ScannedProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The climber whose QR was scanned, as vouched for by the backend. */
  scanned: UserQrCodeVerification;
  /**
   * Gyms the *scanning* climber may register members at. Only gyms where they
   * hold the admin role, since that is who the members API accepts.
   */
  staffGyms: UserStaff[];
  /**
   * The signed code that was scanned. Registering re-submits it so the backend
   * can re-verify rather than trust the client's word that a scan happened.
   */
  scannedCode: string;
  /** Whether the scanner already follows the scanned climber. */
  alreadyFollowing: boolean;
  onFollowed: () => void;
}

/**
 * Shown after landing on a profile with a valid scanned code: offers to add the
 * climber as a friend, and — for staff — to register them at one of their gyms.
 */
export default function ScannedProfileDialog({
  open,
  onOpenChange,
  scanned,
  staffGyms,
  scannedCode,
  alreadyFollowing,
  onFollowed,
}: ScannedProfileDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [following, setFollowing] = useState(alreadyFollowing);
  const [followLoading, setFollowLoading] = useState(false);
  const [registeringSlug, setRegisteringSlug] = useState<string | null>(null);
  const [registeredSlugs, setRegisteredSlugs] = useState<string[]>([]);

  const handleRegister = async (gym: UserStaff) => {
    setRegisteringSlug(gym.gym_slug);
    try {
      await gymMembersService.registerScanned(gym.gym_slug, scanned.slug, scannedCode);
      setRegisteredSlugs((prev) => [...prev, gym.gym_slug]);
      toast({
        title: t('userProfile.scanned.registered'),
        description: t('userProfile.scanned.registeredDescription', {
          name: scanned.full_name,
          gym: gym.gym_name,
        }),
      });
    } catch (error) {
      toast({
        title: t('userProfile.scanned.registerError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setRegisteringSlug(null);
    }
  };

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      await usersService.followUser(scanned.slug);
      setFollowing(true);
      onFollowed();
      toast({
        title: t('userProfile.followSuccess'),
        description: t('userProfile.followSuccessDescription', { name: scanned.full_name }),
      });
    } catch {
      // apiClient already surfaced the error toast.
    } finally {
      setFollowLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('userProfile.scanned.title')}</DialogTitle>
          <DialogDescription>
            {t('userProfile.scanned.description', { name: scanned.full_name })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Button onClick={handleFollow} disabled={following || followLoading}>
            {following ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t('userProfile.scanned.alreadyFriends')}
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                {t('userProfile.scanned.addFriend')}
              </>
            )}
          </Button>

          {staffGyms.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">{t('userProfile.scanned.registerAt')}</p>
              <ul className="flex flex-col gap-2">
                {staffGyms.map((gym) => (
                  <li
                    key={gym.gym_slug}
                    className="flex items-center justify-between gap-2 rounded-md border p-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm">{gym.gym_name}</span>
                    </span>
                    <Button
                      variant={registeredSlugs.includes(gym.gym_slug) ? 'outline' : 'secondary'}
                      size="sm"
                      className="shrink-0"
                      onClick={() => handleRegister(gym)}
                      disabled={
                        registeringSlug === gym.gym_slug || registeredSlugs.includes(gym.gym_slug)
                      }
                    >
                      {registeringSlug === gym.gym_slug && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      {registeredSlugs.includes(gym.gym_slug) && (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      {registeredSlugs.includes(gym.gym_slug)
                        ? t('userProfile.scanned.registered')
                        : t('userProfile.scanned.register')}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
